
import React, { useState, useEffect } from "react";
import {
  FaTrophy,
  FaGamepad,
  FaChartLine,
  FaListOl,
  FaHome,
  FaBars,
  FaUsers,
  FaScroll,
  FaCog,
  FaNewspaper
} from "react-icons/fa";
import logo from "../img/COBRADOR_LEAGUE_LOGO_02_FINAL.png";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { usePlayerMatches } from "../hooks/usePlayerMatches";

const LeagueDashboard = () => {
  const idleague = 1;
  const { divisions } = useLeaderboard(idleague);
  const { matches, fetchMatches } = usePlayerMatches();

  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [playerMatchesMap, setPlayerMatchesMap] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentDivisionId, setCurrentDivisionId] = useState(null);
  
  const handlePlayerClick = async (divisionId, player) => {
    setSelectedPlayers(prev => ({ ...prev, [divisionId]: player }));
    setCurrentDivisionId(divisionId);
    await fetchMatches(player.idplayer);
  };

  useEffect(() => {
    if (currentDivisionId && selectedPlayers[currentDivisionId]) {
      setPlayerMatchesMap(prev => ({ ...prev, [currentDivisionId]: matches }));
    }
  }, [matches]);

  const renderDivision = (division) => {
    const selected = selectedPlayers[division.division];
    const divisionMatches = playerMatchesMap[division.division] || [];

    return (
      <div key={division.division}>
        <h1 className="text-4xl font-bold mb-8 text-center">{division.division}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          <div className="lg:col-span-2 h-full">
            <div className="bg-gray-800 rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Player Statistics</h2>
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-700">
                      <th className="px-4 py-2 text-center">Pos</th>
                      <th className="px-4 py-2 text-left">Player</th>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-center">GP</th>
                      <th className="px-4 py-2 text-center">W</th>
                      <th className="px-4 py-2 text-center">L</th>
                      <th className="px-4 py-2 text-center">D</th>
                      <th className="px-4 py-2 text-center">PS</th>
                      <th className="px-4 py-2 text-center">PC</th>
                      <th className="px-4 py-2 text-center">TP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {division.players.map((player, index) => (
                      <tr
                        key={player.idplayer}
                        className="hover:bg-gray-700 transition-colors duration-200 cursor-pointer"
                        onClick={() => handlePlayerClick(division.division, player)}
                      >
                        <td className="px-4 py-2 text-center">{index + 1}</td>
                        <td className="px-4 py-2">{player.nick}</td>
                        <td className="px-4 py-2 text-gray-500">{player.player}</td>
                        <td className="px-4 py-2 text-center">{player.played}</td>
                        <td className={player.won > 0 ? "text-green-500 px-4 py-2 text-center" : "px-4 py-2 text-center"}>{player.won}</td>
                        <td className={player.lost > 0 ? "text-red-500 px-4 py-2 text-center" : "px-4 py-2 text-center"}>{player.lost}</td>
                        <td className="px-4 py-2 text-center">{player.drawn}</td>
                        <td className="px-4 py-2 text-center">{player.score}</td>
                        <td className="px-4 py-2 text-center">{player.counterscore}</td>
                        <td className="px-4 py-2 text-center font-semibold">{player.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="h-full">
            <div className="bg-gray-800 rounded-lg shadow-lg p-6 h-full overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-4">Player Matches</h2>
              {selected ? (
                <div className="rounded-lg bg-gray-800 p-2 shadow-lg">
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {divisionMatches.length > 0 ? (
                      divisionMatches.map((match) => (
                        <div key={match.id} className="flex items-center justify-between rounded-lg bg-gray-700 p-2">
                          <div className="flex items-center justify-center rounded-lg w-full space-x-4">
                            <div className="text-right basis-1/3">
                              {match.nick1}<br />
                              <p className="text-gray-500">{match.army_p1}</p>
                            </div>
                            <div className={(match.score_p1 > match.score_p2 ? "text-green-400" : match.score_p1 < match.score_p2 ? "text-red-400" : "") + " text-xl font-bold"}>{match.score_p1}</div>
                            <div>vs</div>
                            <div className={(match.score_p1 < match.score_p2 ? "text-green-400" : match.score_p1 > match.score_p2 ? "text-red-400" : "") + " text-xl font-bold"}>{match.score_p2}</div>
                            <div className="text-left basis-1/3">
                              {match.nick2}<br />
                              <p className="text-gray-500">{match.army_p2}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400">There are no matches for this player.</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Click on a player to see their matches.</p>
              )}
            </div>
          </div>
        </div>
        <br /><br />
      </div>
    );
  };

  const menuItems = [
    { name: "WH40k League", icon: FaHome, link: "/wh40k" },
    { name: "AOS League", icon: FaHome, link: "/" },
    { name: "Leaderboard", icon: FaTrophy, link: "/leaderboard1" },
    { name: "Matches", icon: FaGamepad, link: "/matches" },
    { name: "Players", icon: FaUsers },
    { name: "Rules", icon: FaScroll },
    { name: "Settings", icon: FaCog },
  ];

  const quickLinks = [
    { name: "Tournament Schedule", icon: FaGamepad },
    { name: "Army Lists", icon: FaScroll },
    { name: "Community Forum", icon: FaUsers },
    { name: "News & Updates", icon: FaNewspaper },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img src={logo} alt="El Cobrador League Logo" className="w-10 h-10 rounded-full" />
            <h1 className="text-2xl font-bold">El Cobrador League - Warhammer 40k</h1>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-white">
            <FaBars size={24} />
          </button>
          <nav className={`lg:flex space-x-4 ${menuOpen ? "block" : "hidden"}`}>
            {menuItems.map((item, index) => (
              <a key={index} href={item.link} className="flex items-center space-x-1 text-gray-300 hover:text-white transition duration-200">
                <item.icon className="text-sm" />
                <span>{item.name}</span>
              </a>
            ))}
          </nav>
        </div>
      </header>
      <div className="w-full">
        <center>
          <img src="https://c4.wallpaperflare.com/wallpaper/80/896/840/warhammer-40k-4k-popular-wallpaper-thumb.jpg" alt="El Cobrador League Banner" className="w-full h-80 object-cover" />
        </center>
      </div>
      <div className="flex-grow p-8">
        {Array.isArray(divisions) && divisions.map(renderDivision)}
      </div>
      <footer className="bg-gray-800 p-4">
        <div className="container mx-auto">
          <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => (
              <a key={index} href="#" className="flex items-center space-x-2 text-gray-300 hover:text-white transition duration-200">
                <link.icon className="text-sm" />
                <span>{link.name}</span>
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LeagueDashboard;
